import { createContext, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { SoundToggle } from "./SoundToggle";
import { useIsMobile } from "@/hooks/useIsMobile";
import { FEATURED_SONG } from "@/data/site";

// One <audio> for the whole site, mounted above the routes, so the song
// clip keeps playing when the visitor navigates away from the Portal. The
// Home page starts it (scroll on desktop, the tap on mobile) through
// useSiteAudio(); the speaker button is always on screen, on every page,
// so it can be muted from anywhere.
const SiteAudioContext = createContext({ current: null });

export const useSiteAudio = () => useContext(SiteAudioContext);

export const SiteAudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  // Lock the source once it starts so a resize never swaps it mid-song.
  const srcRef = useRef(null);
  const wanted = isMobile ? FEATURED_SONG.audioSrcMobile || FEATURED_SONG.audioSrc : FEATURED_SONG.audioSrc;
  const a = audioRef.current;
  if (!srcRef.current || (a && a.paused && a.currentTime === 0)) srcRef.current = wanted;

  return (
    <SiteAudioContext.Provider value={audioRef}>
      {children}
      {FEATURED_SONG.audioSrc && (
        <>
          <audio ref={audioRef} src={srcRef.current} preload="auto" />
          <SoundToggle
            audioRef={audioRef}
            // Desktop Home only: the first click/tap/key starts the song.
            armOnFirstGesture={!isMobile && pathname === "/"}
            alwaysVisible
            className={isMobile ? "bottom-6 right-6" : "right-6 top-24"}
          />
        </>
      )}
    </SiteAudioContext.Provider>
  );
};
